// SalonHome.tsx
import React, { useEffect, useState } from "react";
// @ts-ignore: allow side-effect CSS import without module declarations
import "./SaloonHome.css";
import StaffSection from "./components/salon-management/staff-section/StaffSection";
import ProductSection from "./components/salon-management/product-section/ProductSection";
import OrderSection from "./components/salon-management/order-section/OrderSection";
import CustomerSelectionDialog from "./components/salon-management/payment-flow/CustomerSelectionDialog";
import CustomerDetailsDialog from "./components/salon-management/payment-flow/CustomerDetailsDialog";
import PaymentMethodDialog from "./components/salon-management/payment-flow/PaymentMethodDialog";
import PaymentSummaryDialog from "./components/salon-management/payment-flow/PaymentSummaryDialog";
import TipsDialog from "./components/salon-management/payment-flow/TipsDialog";
import TabHeader from "./components/salon-management/TabsHeader";
import { useSalonHome } from "./hooks/useSalonHome";
import { PageTitle } from "src/shared/PageTitle";
import ThermalPrinterTest from "src/print-configuration/PrintConfigration";
import SubscriptionDialog from "src/shared/modules/admin/auth/components/SubscriptionDialog";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import LoadingScreen from "src/shared/components/LoadingScreen";

const SalonHome: React.FC = () => {
  const {
    activeTab,
    setActiveTab,
    selectedStaff,
    setSelectedStaff,
    isCustomerDialogOpen,
    setCustomerDialogOpen,
    staffMembers,
    categories,
    products,
    filterProducts,
    filteredCategories,
    handleCategorySelect,
    resetOrderFlow,
    order,
    payment,
    handleConfirmOrder,
    handleCustomerSelect,
    handleAddCustomer,
    handleCompletePayment,
    resetCategoryFlag,
    setResetCategoryFlag,
    print,
    resetCategoryFilter,
    loading,
    error,
    outstandingBalance,
    isLoadingOutstanding,
    isProcessingOrder,
  } = useSalonHome();

  const serviceCategories = categories.filter((c) => c.type === "Services");

  const { subscriptionInfo } = useUser();
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState<React.ReactNode>("");
  
  const backToStaff = () => {
    setSelectedStaff(null);
    setResetCategoryFlag(true);
    resetCategoryFilter();
  };

  useEffect(() => {
    document.title = pageTitle("Salon › Home");
  }, []);

  useEffect(() => {
    if (!subscriptionInfo) return;

    const alreadyShown = sessionStorage.getItem("subscription_dialog_shown");
    if (alreadyShown === "true") return;

    if (subscriptionInfo.status === "grace_period") {
      const days = subscriptionInfo.daysRemaining;

      const formattedMessage = (
        <>
          This is a reminder that your current subscription will expire in <strong>{days} day(s)</strong>. Please renew now to avoid service disruption.
        </>
      );

      setMessage(formattedMessage);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }

    if (subscriptionInfo.warning) {
      setMessage(subscriptionInfo.warning);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }
  }, [subscriptionInfo]);

  // ✅ Show loading overlay when processing order
  if (isProcessingOrder) {
    return (
      <div className="service-container">
        <LoadingScreen message="Processing your order, please wait..." />
      </div>
    );
  }

  return (
    <div className="service-container">
      <PageTitle title="- Point of Sale › Home" />
      {loading && <LoadingScreen message="Please wait, Loading Home..." />}
      {!loading && !error && (
        <>
          <TabHeader activeTab={activeTab} setActiveTab={setActiveTab} resetOrderFlow={resetOrderFlow} />

          <div className="service-content">
            {activeTab === "Staffs" ? (
              <StaffSection
                staffMembers={staffMembers}
                selectedStaff={selectedStaff}
                categories={serviceCategories}
                onStaffSelect={setSelectedStaff}
                onCategorySelect={handleCategorySelect}
                onClearSelection={backToStaff}
                resetStaffCategory={resetCategoryFlag}
              />
            ) : (
              <ProductSection categories={filteredCategories} products={products} onCategorySelect={handleCategorySelect} />
            )}

            <div className="product-order-section">
              <ProductSection products={filterProducts} onProductSelect={order.addProduct} displayMode="grid" selectedProducts={order.selectedProducts} />

              <OrderSection
                selectedProducts={order.selectedProducts}
                discount={order.discount}
                onQuantityChange={order.updateProductQuantity}
                onPriceChange={order.updateProductPrice}  
                onRemoveProduct={order.removeProduct}
                onDiscountChange={order.setDiscount}
                onConfirmOrder={handleConfirmOrder}
                onCancelOrder={resetOrderFlow}
              />
            </div>
          </div>

          {/* Dialogs */}
          <CustomerSelectionDialog
            open={payment.customerAdd}
            onClose={() => payment.setCustomerAdd(false)}
            onCustomerSelect={handleCustomerSelect}
            onAddNewCustomer={() => {
              payment.setCustomerAdd(false);
              setCustomerDialogOpen(true);
            }}
          />

          <CustomerDetailsDialog
            open={isCustomerDialogOpen}
            onClose={() => setCustomerDialogOpen(false)}
            customerName={payment.customerName}
            customerPhone={payment.customerPhone}
            onCustomerNameChange={payment.setCustomerName}
            onCustomerPhoneChange={payment.setCustomerPhone}
            onSaveCustomer={handleAddCustomer}
          />

          <PaymentMethodDialog
            open={payment.openProductList}
            onClose={() => payment.setOpenProductList(false)}
            onBack={() => {
              payment.setOpenProductList(false);
              payment.setCustomerAdd(true);
            }}
            onPaymentMethodSelect={payment.handlePaymentMethodSelect}
          />

          {payment.byTips && (
            <TipsDialog
              open={payment.openTipsList}
              onClose={() => payment.setOpenTipsList(false)}
              onConfirm={() => {
                payment.setOpenTipsList(false);
                payment.setOpenPaymentDetails(true);
              }}
              staff={selectedStaff}
              tipsAmount={payment.tipsAmount}
              onTipsChange={payment.handleTipsChange}
            />
          )}

          <PaymentSummaryDialog
            open={payment.openPaymentDetails}
            onClose={() => payment.setOpenPaymentDetails(false)}
            onBack={() => {
              payment.setOpenPaymentDetails(false);
              payment.setOpenProductList(true);
            }}
            onCompletePayment={handleCompletePayment}
            selectedProducts={order.selectedProducts}
            selectedStaff={selectedStaff}
            paymentMethod={payment.paymentMethod}
            customerName={payment.customerName}
            customerPhone={payment.customerPhone}
            discount={order.discount}
            tipsAmount={payment.tipsAmount}
            receivedAmount={payment.receivedAmount}
            onReceivedAmountChange={payment.setReceivedAmount}
            time={payment.time}
            isProcessing={payment.isProcessingPayment}
            outstandingBalance={outstandingBalance}
            isLoadingOutstanding={isLoadingOutstanding}
          />

          {print && (
            <ThermalPrinterTest
              key={print.printId}
              PrintData={print}
              openPrint={String(print.printId)}
            />
          )}

          <SubscriptionDialog
            open={showDialog}
            message={message}
            onClose={() => setShowDialog(false)}
            onRenew={() => {
              setShowDialog(false);
            }}
          />
        </>
      )}
    </div>
  );
};

export default SalonHome;